import _ from 'lodash';
import { useEffect, useState } from 'react';
import { PREFERENCE_UPSERT_SUCCESS_HTTP_STATUS, TOAST_TYPES } from '../constants/settings.constants';
import { DEFAULT_PAGE_SIZE, ORDER_CHANGE_DIRECTION } from '../constants/table.constants';
import { users } from '../services/bff.api';
import toastService from '../services/toasts.service';

const _fieldsToApi = (fields) => fields?.map((f) => _.pick(f, ['value', 'order', 'sort', 'show']));

export const usePreferences = (user, resource) => {
  const [shouldFetch, setShouldFetch] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [fields, setFields] = useState(resource?.table?.fields ?? []);
  const [sort, setSort] = useState(resource?.table?.defaultSort);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

  const _handleUpsertDone = (resp, onSuccess) => {
    if (resp?.status === PREFERENCE_UPSERT_SUCCESS_HTTP_STATUS) {
      onSuccess();
    } else {
      toastService.showToast(TOAST_TYPES.WARNING, 'We kunnen uw voorkeuren niet bijwerken.');
    }
    setIsFetched(true);
  };

  const onOrderField = (field, direction) => {
    const currentField = fields?.find((f) => f.value === field);
    if (
      !currentField ||
      (currentField?.order === 0 && direction === ORDER_CHANGE_DIRECTION.UP) ||
      (currentField?.order === fields.length - 1 && direction === ORDER_CHANGE_DIRECTION.DOWN)
    ) {
      return;
    }

    let newFields = [...fields];
    let changed = false;
    if (direction === ORDER_CHANGE_DIRECTION.UP) {
      newFields = newFields?.map((f) => {
        if (f.value === currentField.value) {
          return { ...f, order: f.order - 1 };
        }
        if (f.order === currentField.order - 1) {
          return { ...f, order: f.order + 1 };
        }
        return f;
      });
      changed = true;
    }

    if (direction === ORDER_CHANGE_DIRECTION.DOWN) {
      newFields = newFields?.map((f) => {
        if (f.value === currentField.value) {
          return { ...f, order: f.order + 1 };
        }
        if (f.order === currentField.order + 1) {
          return { ...f, order: f.order - 1 };
        }
        return f;
      });
      changed = true;
    }

    if (changed) {
      setIsFetched(false);
      users
        .upsertPreferences(user.userName, resource.key, { fields: _fieldsToApi(newFields) })
        .then((resp) => _handleUpsertDone(resp, () => setFields(newFields)));
    }
  };

  const onEnableField = (field, enabled) => {
    const newFields = fields?.map((f) => (f.value === field ? { ...f, show: enabled } : f));
    setIsFetched(false);
    users
      .upsertPreferences(user.userName, resource.key, { fields: _fieldsToApi(newFields) })
      .then((resp) => _handleUpsertDone(resp, () => setFields(newFields)));
  };

  const onSortChange = (newSort) => {
    if (sort !== newSort) {
      setIsFetched(false);
      users
        .upsertPreferences(user.userName, resource.key, { sort: newSort })
        .then((resp) => _handleUpsertDone(resp, () => setSort(newSort)));
    }
  };

  const onPageSizeChange = (newSize) => {
    if (newSize !== size) {
      setIsFetched(false);
      users
        .upsertPreferences(user.userName, resource.key, { size: newSize })
        .then((resp) => _handleUpsertDone(resp, () => setSize(newSize)));
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted && shouldFetch && user.userName && resource.key) {
      setIsFetched(false);
      users.getPreferences(user.userName, resource.key).then((resp) => {
        if (!isMounted) {
          return;
        }
        setShouldFetch(false);
        if (resp?.json?.fields) {
          setFields(
            fields?.map((f) => {
              const fieldInPreference = resp.json.fields?.find((prefF) => prefF.value === f.value);
              if (fieldInPreference) {
                return {
                  ...f,
                  order: fieldInPreference?.order,
                  show: fieldInPreference?.show
                };
              }
              return f;
            })
          );
        }
        if (resp?.json?.sort) {
          setSort(resp.json.sort);
        }
        if (resp?.json?.size) {
          setSize(resp.json.size);
        }
        setIsFetched(true);
      });
    }

    return () => {
      isMounted = false;
    };
  }, [fields, shouldFetch, user.userName, resource.key]);

  if (!user?.userName) {
    return { fields, sort, size, isFetched: false };
  }

  return { fields, sort, size, isFetched, onSortChange, onPageSizeChange, onEnableField, onOrderField };
};
